/**
 * Booqable Thank You
 * Hydrates the thank-you page with order details persisted in sessionStorage
 * by booqable-checkout.js after a successful order submission.
 */

class BooqableThankyou extends HTMLElement {
    constructor() {
        super();
        this.currency = { symbol: '£', precision: 2 };
    }

    connectedCallback() {
        const data = this.readOrder();

        if (!data || !data.cart) {
            this.showEmpty();
            return;
        }

        this.renderName(data.form, data.displayName);
        this.renderMeta(data.form, data.cart);
        this.renderItems(data.cart);
        this.renderTotals(data.cart);

        this.querySelector('[data-bind="summary"]').hidden = false;
    }

    readOrder() {
        try {
            const raw = sessionStorage.getItem('bqOrderConfirmation');
            return raw ? JSON.parse(raw) : null;
        } catch (e) {
            console.warn('[BooqableThankyou] Could not parse order data:', e);
            return null;
        }
    }

    showEmpty() {
        const empty = this.querySelector('[data-bind="empty"]');
        if (empty) empty.hidden = false;
    }

    renderName(form, displayName) {
        if (!form) return;
        const nameEl = this.querySelector('[data-bind="full-name"]');
        // `displayName` covers signed-in customers, whose identity inputs
        // were disabled and therefore absent from the submitted form.
        const name = (form['Full Name'] || form.full_name || displayName || '').trim();
        if (nameEl && name) nameEl.textContent = name;
    }

    renderMeta(form, cart) {
        const dateEl = this.querySelector('[data-bind="date-range"]');
        if (dateEl) {
            if (cart.starts_at && cart.stops_at) {
                dateEl.innerHTML = `${this.formatDate(cart.starts_at)} <span class="BooqableThankyou__Arrow">&rarr;</span> ${this.formatDate(cart.stops_at)}`;
            } else {
                dateEl.textContent = 'Dates to be confirmed';
            }
        }

        const delivery = form && (form['Delivery Address'] || form.delivery_address);
        if (delivery) {
            this.querySelector('[data-bind="delivery-row"]').hidden = false;
            this.querySelector('[data-bind="delivery-address"]').textContent = delivery;
        }

        const guests = form && (form['Guest Count'] || form.guest_count);
        if (guests) {
            this.querySelector('[data-bind="guests-row"]').hidden = false;
            this.querySelector('[data-bind="guest-count"]').textContent = guests;
        }
    }

    renderItems(cart) {
        const tbody = this.querySelector('[data-bind="items"]');
        if (!tbody) return;

        const lines = Array.isArray(cart.lines) ? cart.lines : [];
        if (!lines.length) {
            tbody.innerHTML = '<tr><td colspan="3" class="BooqableThankyou__EmptyRow">No items recorded</td></tr>';
            return;
        }

        tbody.innerHTML = lines.map((line) => `
            <tr class="BooqableThankyou__Row">
                <td class="BooqableThankyou__ItemCell">
                    ${line.photo_url ? `<img class="BooqableThankyou__ItemImg" src="${line.photo_url}" alt="">` : ''}
                    <span class="BooqableThankyou__ItemName">${this.escape(line.title || 'Item')}</span>
                </td>
                <td class="BooqableThankyou__ColQty">${line.quantity ?? 1}</td>
                <td class="BooqableThankyou__ColPrice">${this.formatMoney(line.price_in_cents)}</td>
            </tr>
        `).join('');
    }

    renderTotals(cart) {
        this.setText('subtotal', this.formatMoney(cart.price_in_cents));
        const grand = cart.grand_total_with_tax_in_cents ?? cart.grand_total_in_cents ?? cart.price_in_cents;
        this.setText('total', this.formatMoney(grand));
    }

    setText(bind, value) {
        const el = this.querySelector(`[data-bind="${bind}"]`);
        if (el) el.textContent = value;
    }

    formatDate(value) {
        const d = new Date(value);
        if (isNaN(d.getTime())) return '—';
        return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
    }

    formatMoney(cents) {
        if (cents == null) return '—';
        return `${this.currency.symbol}${(cents / 100).toFixed(this.currency.precision)}`;
    }

    escape(str) {
        return String(str).replace(/[&<>"']/g, (c) => ({
            '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
        }[c]));
    }
}

if (!customElements.get('booqable-thankyou')) {
    customElements.define('booqable-thankyou', BooqableThankyou);
}
